import {
	Box,
	Card,
	CardContent,
	Chip,
	Grid,
	Stack,
	Typography,
} from "@mui/material";
import { safeBoolean } from "../../utils/CommonFunctions";
import {
	MyBusinessIcon,
	MyCancelIcon,
	MyCheckCircleIcon,
	MyCheckIcon,
	MyCreditCardIcon,
	MyEmailIcon,
	MyLanguageIcon,
	MyPersonIcon,
	MyPhoneIcon,
	MyVerifiedIcon,
	MyCalendarIcon,
	MyLocationIcon,
	MyMapIcon,
	MyAccountBalanceWalletIcon,
	MyAttachMoneyIcon,
	MyVisibilityIcon,
	MyVisibilityOffIcon,
	MyInfoIcon,
} from "../../Assets/svg/MyIcons";

const TenantCreationPreview = ({
	previewData,
	planMapPrice,
	selectedCurrency,
	tenantCurrencies,
	merchantDetails,
	isPlanChanged = false,
	kycCountryList = [],
	kycPlanList = [],
	tripolaPlanList,
	type
}) => {
	// console.log("Preview data", previewData)
	// console.log("TYPE RECEIVED:", type);
	const selectedPlan = planMapPrice?.[previewData?.qbotPlan?.planId] || {};
	const currentPlanDetails = selectedPlan;
	const currentAmount =
		previewData?.qbotPlan?.billingCycle === "monthly"
			? selectedPlan?.monthly
			: selectedPlan.yearly;
	const currencyDetails = tenantCurrencies?.find(
		(c) => c.id === Number(selectedCurrency),
	) || { exchangeRate: "1.0000", symbol: "CI$" };

	const selectedKycPlan = kycPlanList.find(
		(plan) => String(plan.id) === String(previewData?.kyc?.kycPlanId)
	);

	const selectedTripolaPlan =
		tripolaPlanList?.find((plan) => String(plan.id) === String(previewData?.tripolaPlan?.tripolaPlanId)) || {};

	let noteText = "";
	if (
		previewData?.qbotPlan?.planHistory &&
		previewData?.qbotPlan.planHistory[0].paymentMode !== previewData?.qbotPlan.paymentMode &&
		previewData?.qbotPlan.paymentMode === "online"
	) {
		noteText =
			"The payment mode will be changed to Online. A payment link will be sent to the tenant’s email two days before the current plan expires.";
	} else if (
		previewData?.qbotPlan?.planHistory &&
		previewData?.qbotPlan.planHistory[0].paymentMode !== previewData?.qbotPlan.paymentMode &&
		previewData?.qbotPlan.paymentMode === "offline"
	) {
		noteText =
			"The payment mode will be changed to Offline. The admin must manually verify the payment after it is received from the tenant.";
	} else if (
		previewData?.qbotPlan?.planHistory &&
		previewData?.qbotPlan.planHistory[0].paymentMode === previewData?.qbotPlan.paymentMode &&
		previewData?.qbotPlan.isAutoPay
	) {
		if (
			previewData?.qbotPlan.planHistory &&
			previewData?.qbotPlan.planHistory[0].paymentMode === "online"
		) {
			noteText =
				"Subscription is enabled for this tenant. The payment will be automatically deducted on the due date.";
		} else {
			noteText =
				"Subscription is enabled for this tenant. The admin must manually verify the payment once it is received from the tenant on the due date.";
		}
	} else {
		noteText =
			"Subscription is disabled for this tenant. The admin must manually process the payment on the due date.";
	}

	const getFeatureValue = (item) => {
		const featureName = (item?.feature || "").toLowerCase();
		let value;

		// For custom plans, check custom limits and handle null as "Unlimited"
		if (previewData?.qbotPlan?.isCustomPlan && previewData?.qbotPlan?.customPlanDetails) {
			if (
				featureName.includes("user") &&
				previewData?.qbotPlan.customPlanDetails.userLimit !== undefined
			) {
				value =
					previewData?.qbotPlan.customPlanDetails.userLimit === null
						? "Unlimited"
						: previewData?.qbotPlan.customPlanDetails.userLimit;
			} else if (
				featureName.includes("message") &&
				previewData?.qbotPlan.customPlanDetails.messageLimit !== undefined
			) {
				value =
					previewData?.qbotPlan.customPlanDetails.messageLimit === null
						? "Unlimited"
						: previewData?.qbotPlan.customPlanDetails.messageLimit;
			} else if (
				featureName.includes("agent") &&
				previewData?.qbotPlan.customPlanDetails.agentLimit !== undefined
			) {
				value =
					previewData?.qbotPlan.customPlanDetails.agentLimit === null
						? "Unlimited"
						: previewData?.qbotPlan.customPlanDetails.agentLimit;
			} else {
				value =
					item?.value === null ? "Unlimited" : (item?.value ?? "");
			}
		} else {
			// Return the plan's default feature value (handle null as "Unlimited" too)
			value = item?.value === null ? "Unlimited" : (item?.value ?? "");
		}

		// Capitalize "unlimited" if it comes from backend as lowercase
		if (typeof value === "string" && value.toLowerCase() === "unlimited") {
			return "Unlimited";
		}
		return value;
	};

	const InfoRow = ({ label, value, icon }) => (
		<Box sx={{ py: 1 }}>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
				{label}
			</Typography>
			<Stack direction="row" spacing={1} alignItems="center">
				{icon && (
					<Box
						sx={{
							color: "var(--bs-primary)",
							display: "flex",
							minWidth: 20,
						}}
					>
						{icon}
					</Box>
				)}
				<Typography variant="body1"
					sx={{
						fontWeight: 600,
						wordBreak: "break-word",
						overflowWrap: "anywhere",
						minWidth: 0
					}}>
					{value || "-"}
				</Typography>
			</Stack>
		</Box>
	);

	// const isQbot = previewData?.common?.isQbot === true || previewData?.common?.isQbot === 1 || type === "qbot";
	// const isPaycentral = previewData?.common?.isPaycentral === true || previewData?.common?.isPaycentral === 1 || type === "paycentral";
	// const isKyc = previewData?.common?.isKyc === true || previewData?.common?.isKyc === 1 || type === "kyc";
	// const isTripola = previewData?.common?.isTripola === true || previewData?.common?.isTripola === 1 || type === "tripola";

	const isQbot = type === "qbot";
	const isPaycentral = type === "paycentral";
	const isKyc = type === "kyc";
	const isTripola = type === "tripola";

	const selectedKycCountry = kycCountryList.find(
		(country) => country.code === previewData?.kyc?.kycCountry
	);

	return (
		<Grid container spacing={2}>
			{/* Tenant Information Card */}
			<Grid item xs={12}>
				<Card
					sx={{
						height: "100%",
						boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
					}}
				>
					<CardContent sx={{ p: 3 }}>
						<Stack
							direction="row"
							alignItems="center"
							spacing={1.5}
							sx={{ mb: 2 }}
						>
							<Box
								sx={{
									bgcolor: "var(--bs-primary)",
									p: 1,
									borderRadius: 1,
									display: "flex",
								}}
							>
								<MyPersonIcon
									sx={{ fontSize: 24, color: "white" }}
								/>
							</Box>
							<Typography variant="h6" fontWeight={600}>
								Tenant Information
							</Typography>
						</Stack>

						{/* Personal Details */}
						<Box
							sx={{
								bgcolor: "grey.50",
								p: 2.5,
								borderRadius: 1,
								mb: 3,
								boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
							}}
						>
							<Typography
								variant="subtitle1"
								fontWeight={600}
								color="var(--bs-primary)"
								sx={{ mb: 1.5 }}
							>
								Personal Details
							</Typography>
							<Grid container spacing={2}>
								{[
									{
										label: "First Name",
										value: previewData?.common.firstName,
										icon: <MyPersonIcon fontSize="small" />,
										cols: 6,
									},
									{
										label: "Last Name",
										value: previewData?.common.lastName,
										icon: <MyPersonIcon fontSize="small" />,
										cols: 6,
									},
									{
										label: "Email",
										value: previewData?.common.email,
										icon: <MyEmailIcon fontSize="small" />,
										cols: 12,
									},
								].map((item, index) => (
									<Grid
										item
										xs={12}
										sm={item.cols}
										key={index}
									>
										<InfoRow
											label={item.label}
											value={item.value}
											icon={item.icon}
										/>
									</Grid>
								))}
							</Grid>
						</Box>
					</CardContent>
				</Card>
			</Grid>

			<Grid item xs={12} md={8}>
				{/* Company Details */}
				{isQbot && (
					<Box
						sx={{
							bgcolor: "grey.50",
							p: 2.5,
							borderRadius: 1,
							mb: 3,
							boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
						}}
					>
						<Typography
							variant="subtitle1"
							fontWeight={600}
							color="var(--bs-primary)"
							sx={{ mb: 1.5 }}
						>
							Qbot Company Details
						</Typography>
						<Grid container spacing={2}>
							{[
								{
									label: "Company Name",
									value: previewData?.qbot?.companyName,
									icon: (
										<MyBusinessIcon fontSize="small" />
									),
									cols: 6,
								},
								{
									label: "Company Email",
									value: previewData?.qbot?.companyEmail,
									icon: <MyEmailIcon fontSize="small" />,
									cols: 6,
								},
								{
									label: "Domain URL",
									value: previewData?.qbot?.domainUrl,
									icon: (
										<MyLanguageIcon fontSize="small" />
									),
									cols: 6,
								},
								{
									label: "Phone Number",
									value: previewData?.qbot?.companyPhoneNumber,
									icon: <MyPhoneIcon fontSize="small" />,
									cols: 6,
								},
								{
									label: "Address",
									value: previewData?.common.companyAddress,
									icon: (
										<MyBusinessIcon fontSize="small" />
									),
									cols: 12,
								},
								{
									label: "Zip Code",
									value: previewData?.qbot?.companyZipCode,
									icon: (
										<MyLocationIcon fontSize="small" />
									),
									cols: 4,
								},
								{
									label: "Country",
									value: previewData?.common.companyCountry,
									icon: (
										<MyLanguageIcon fontSize="small" />
									),
									cols: 4,
								},
								{
									label: "State",
									value: previewData?.common.companyState,
									icon: <MyMapIcon fontSize="small" />,
									cols: 4,
								},
							].map((item, index) => (
								<Grid
									item
									xs={12}
									sm={item.cols}
									key={index}
								>
									<InfoRow
										label={item.label}
										value={item.value}
										icon={item.icon}
									/>
								</Grid>
							))}
						</Grid>
					</Box>
				)}

				{/* Tenant Configuration */}
				{isQbot && (
					<Box
						sx={{
							bgcolor: "grey.50",
							p: 2.5,
							borderRadius: 1,
							mb: 3,
							boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
						}}
					>
						<Typography
							variant="subtitle1"
							fontWeight={600}
							color="var(--bs-primary)"
							sx={{ mb: 1.5 }}
						>
							Tenant Configuration
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={4}>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 0.5 }}
								>
									Domain Verification
								</Typography>
								<Chip
									icon={
										previewData?.qbot?.isVerified ? (
											<MyVerifiedIcon />
										) : (
											<MyCancelIcon />
										)
									}
									label={
										previewData?.qbot?.isVerified
											? "Verified"
											: "Unverified"
									}
									color={
										previewData?.qbot?.isVerified
											? "success"
											: "error"
									}
									size="medium"
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 0.5 }}
								>
									Subscription Status
								</Typography>
								<Chip
									icon={
										safeBoolean(
											previewData?.qbotPlan?.isAutoPay,
										) ? (
											<MyCheckCircleIcon className="text-success" />
										) : (
											<MyCancelIcon className="text-danger" />
										)
									}
									label={
										safeBoolean(previewData?.qbotPlan?.isAutoPay)
											? "Enabled"
											: "Disabled"
									}
									className={
										safeBoolean(previewData?.qbotPlan?.isAutoPay)
											? "text-success"
											: "text-danger"
									}
									sx={{ fontWeight: 600 }}
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 0.5 }}
								>
									Currency
								</Typography>
								<Chip
									icon={
										<Typography
											component="span"
											sx={{
												fontSize: "1.4rem",
												fontWeight: 600,
												lineHeight: 1,
											}}
										>
											{currencyDetails?.symbol}
										</Typography>
									}
									label={
										currencyDetails?.name ||
										currencyDetails?.currencyName ||
										""
									}
									size="medium"
									sx={{
										backgroundColor:
											"var(--bs-primary)",
										color: "white",
										"& .MuiChip-icon": {
											color: "white",
											marginLeft: "10px",
										},
										"& .MuiChip-label": {
											px: 2,
										},
									}}
								/>
							</Grid>

							{previewData?.qbotPlan?.paymentMode === "offline" && (
								<Grid item xs={12} sm={12}>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 0.5 }}
									>
										Billing Details Visibility
									</Typography>
									<Chip
										icon={
											previewData?.qbotPlan?.hideBillingDetails ? (
												<MyVisibilityOffIcon />
											) : (
												<MyVisibilityIcon />
											)
										}
										label={
											previewData?.qbotPlan?.hideBillingDetails
												? "Hidden - Tenant cannot view billing details"
												: "Visible - Tenant can view billing details"
										}
										size="medium"
										sx={{
											fontWeight: 600,
											minWidth: "300px",
											justifyContent: "flex-start",
										}}
									/>
								</Grid>
							)}
						</Grid>
					</Box>
				)}

				{/* Allowed Features */}
				{isQbot && (
					<Box
						sx={{
							bgcolor: "grey.50",
							p: 2.5,
							borderRadius: 1,
							mt: 3,
							boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
						}}
					>
						<Typography
							variant="subtitle1"
							fontWeight={600}
							color="var(--bs-primary)"
							sx={{ mb: 1.5 }}
						>
							Allowed Features
						</Typography>
						<Grid container spacing={2}>
							{[
								{ label: "User Chat", key: "user_chat" },
								{ label: "AI Chat", key: "ai_chat" },
								{
									label: "Support Ticket",
									key: "support_tickets",
								},
								{
									label: "Web Mail Tickets",
									key: "web_mail_tickets",
								},
							].map((feature) => {
								const isEnabled = safeBoolean(
									previewData?.qbotPlan?.allowedFeatures?.[
									feature.key
									],
								);
								return (
									<Grid item xs={3} key={feature.key}>
										<Box sx={{ py: 1 }}>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ mb: 0.5 }}
											>
												{feature.label}
											</Typography>
											<Stack
												direction="row"
												spacing={1}
												alignItems="center"
											>
												{isEnabled ? (
													<MyCheckCircleIcon
														fontSize="small"
														className="text-success"
													/>
												) : (
													<MyCancelIcon
														fontSize="small"
														className="text-danger"
													/>
												)}
												<Typography
													variant="body1"
													fontWeight={600}
												>
													{isEnabled
														? "Enabled"
														: "Disabled"}
												</Typography>
											</Stack>
										</Box>
									</Grid>
								);
							})}
						</Grid>
					</Box>
				)}

			</Grid>

			{/* Plan Information Card */}
			{isQbot && (
				<Grid item xs={12} md={4}>
					<Card
						sx={{
							height: "100%",
							background:
								"linear-gradient(135deg, #466bb3 0%, #22419e 100%)",
							boxShadow: "0 20px 60px rgba(34, 65, 158, 0.3)",
						}}
					>
						<CardContent sx={{ p: 3 }}>
							<Stack
								direction="row"
								alignItems="center"
								spacing={1.5}
								sx={{ mb: 2 }}
							>
								<Box
									sx={{
										bgcolor: "white",
										p: 1,
										borderRadius: 1,
										display: "flex",
									}}
								>
									<MyCreditCardIcon
										sx={{
											fontSize: 24,
											color: "var(--bs-primary)",
										}}
									/>
								</Box>
								<Typography
									variant="h6"
									fontWeight={600}
									sx={{ color: "white" }}
								>
									Qbot Plan Information
								</Typography>
							</Stack>

							{/* Plan Details */}
							<Box
								sx={{
									bgcolor: "rgba(255,255,255,0.15)",
									p: 2,
									borderRadius: 1,
									mb: 2,
									backdropFilter: "blur(10px)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									sx={{ mb: 1.5, color: "white" }}
								>
									Plan Details{isPlanChanged ? " (upcoming)" : ""}
								</Typography>
								<Stack spacing={1.5}>
									{[
										{
											label: "Plan Name",
											value: previewData?.qbotPlan?.planId
												? `${selectedPlan?.name}${previewData?.qbotPlan?.isCustomPlan
													? " (Custom)"
													: ""
												}`
												: "-",
											icon: (
												<MyCreditCardIcon fontSize="small" />
											),
											variant: "body1",
										},
										{
											label: "Plan Price",
											value: previewData?.qbotPlan?.planId
												? `${currencyDetails?.symbol} ${previewData?.qbotPlan?.isCustomPlan &&
													previewData?.qbotPlan
														.customPlanDetails
														?.price !== null &&
													previewData?.qbotPlan
														.customPlanDetails
														?.price !== undefined
													? Number(
														previewData
															.qbotPlan
															.customPlanDetails
															.price,
													).toFixed(2)
													: currentAmount
														? Number(
															Number(
																currentAmount,
															) *
															Number(
																currencyDetails?.exchangeRate,
															),
														).toFixed(2)
														: "0.00"
												}`
												: "-",
											icon: (
												<MyAccountBalanceWalletIcon fontSize="small" />
											),
											variant: "h5",
										},
									].map((item, index) => (
										<Box sx={{ py: 1 }} key={index}>
											<Typography
												variant="body2"
												sx={{
													color: "rgba(255,255,255,0.8)",
													display: "block",
													mb: 0.5,
												}}
											>
												{item.label}
											</Typography>
											<Stack
												direction="row"
												spacing={1}
												alignItems="center"
											>
												<Box
													sx={{
														color: "white",
														display: "flex",
														minWidth: 20,
													}}
												>
													{item.icon}
												</Box>
												<Typography
													variant={item.variant}
													fontWeight={
														item.variant === "h5"
															? 700
															: 600
													}
													sx={{ color: "white" }}
												>
													{item.value}
												</Typography>
											</Stack>
										</Box>
									))}
								</Stack>
							</Box>

							{/* Billing Information */}
							<Box
								sx={{
									bgcolor: "rgba(255,255,255,0.15)",
									p: 2,
									borderRadius: 1,
									mb: 2,
									backdropFilter: "blur(10px)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									sx={{ mb: 1.5, color: "white" }}
								>
									Billing Information
									{isPlanChanged ? " (upcoming)" : ""}
								</Typography>
								<Stack spacing={1.5}>
									{[
										{
											label: "Payment Mode",
											value: previewData?.qbotPlan?.paymentMode || "-",
											icon: (
												<MyCreditCardIcon fontSize="small" />
											),
										},
										{
											label: "Billing Cycle",
											value: previewData?.qbotPlan?.billingCycle || "-",
											icon: (
												<MyCalendarIcon fontSize="small" />
											),
										},
									].map((item, index) => (
										<Box sx={{ py: 1 }} key={index}>
											<Typography
												variant="body2"
												sx={{
													color: "rgba(255,255,255,0.8)",
													display: "block",
													mb: 0.5,
												}}
											>
												{item.label}
											</Typography>
											<Stack
												direction="row"
												spacing={1}
												alignItems="center"
											>
												<Box
													sx={{
														color: "white",
														display: "flex",
														minWidth: 20,
													}}
												>
													{item.icon}
												</Box>
												<Typography
													variant="body1"
													fontWeight={600}
													sx={{
														textTransform: "capitalize",
														color: "white",
													}}
												>
													{item.value}
												</Typography>
											</Stack>
										</Box>
									))}
								</Stack>
							</Box>

							{/* Plan Features Box */}
							{currentPlanDetails?.features && (
								<Box
									sx={{
										bgcolor: "rgba(255,255,255,0.15)",
										p: 2,
										borderRadius: 1,
										backdropFilter: "blur(10px)",
									}}
								>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										sx={{ mb: 1.5, color: "white" }}
									>
										Plan Features
										{isPlanChanged ? " (upcoming)" : ""}
									</Typography>
									<Stack spacing={1.5}>
										{currentPlanDetails?.features?.map(
											(item, index) => {
												const value = getFeatureValue(item);

												const featureName = (
													item?.feature || ""
												).toLowerCase();
												// Determine which icon to use based on feature name
												let FeatureIcon = MyCheckIcon;
												if (featureName.includes("user")) {
													FeatureIcon = MyPersonIcon;
												} else if (
													featureName.includes("message")
												) {
													FeatureIcon = MyEmailIcon;
												} else if (
													featureName.includes("agent")
												) {
													FeatureIcon = MyCheckCircleIcon;
												}

												return (
													<Box key={index} sx={{ py: 1 }}>
														<Typography
															variant="body2"
															sx={{
																color: "rgba(255,255,255,0.8)",
																mb: 0.5,
															}}
														>
															{item.feature}
														</Typography>
														<Stack
															direction="row"
															spacing={1}
															alignItems="center"
														>
															<FeatureIcon fontSize="small" />
															<Typography
																variant="body1"
																fontWeight={600}
																sx={{
																	color: "white",
																}}
															>
																{value || "-"}
															</Typography>
														</Stack>
													</Box>
												);
											},
										)}
									</Stack>
								</Box>
							)}

							{/* Note Section */}
							{previewData?.qbot?.id && (
								<Box
									sx={{
										bgcolor: "rgba(255,255,255,0.15)",
										p: 2,
										borderRadius: 1,
										mt: 2,
										backdropFilter: "blur(10px)",
									}}
								>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										sx={{ mb: 0, color: "white" }}
									>
										Note
									</Typography>
									<Stack spacing={1.5}>
										<Box sx={{ py: 1 }}>
											<Typography
												variant="body2"
												className="display-block text-white"
											>
												{noteText}
											</Typography>
										</Box>
									</Stack>
								</Box>
							)}
						</CardContent>
					</Card>
				</Grid>
			)}

			{/* PayCentral Corporation Information */}
			{isPaycentral && (
				<Grid item xs={12} md={8}>
					<Card
						sx={{
							height: "100%",
							boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
						}}
					>
						<CardContent sx={{ p: 3 }}>
							<Stack
								direction="row"
								alignItems="center"
								spacing={1.5}
								sx={{ mb: 2 }}
							></Stack>

							<Box
								sx={{
									bgcolor: "grey.50",
									p: 2.5,
									borderRadius: 1,
									mb: 3,
									boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									color="var(--bs-primary)"
									sx={{ mb: 1.5 }}
								>
									Paycentral Corporation Information
								</Typography>

								<Grid container spacing={2}>
									{[
										{
											label: "User Name",
											value: previewData?.paycentral?.userName,
											icon: <MyPersonIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Corporation Name",
											value: previewData?.paycentral?.organizationName,
											icon: <MyBusinessIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Domain",
											value: previewData?.paycentral?.domainUrl,
											icon: <MyLanguageIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Business Name",
											value: previewData?.paycentral?.businessName,
											icon: <MyBusinessIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Business Type",
											value: previewData?.paycentral?.businessType,
											icon: <MyBusinessIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Business Country",
											value: previewData?.paycentral?.businessCountry,
											icon: <MyLanguageIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Business State",
											value: previewData?.paycentral?.businessState,
											icon: <MyMapIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Website",
											value: previewData?.paycentral?.website,
											icon: <MyLanguageIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Tax ID",
											value: previewData?.paycentral?.taxId,
											icon: <MyBusinessIcon fontSize="small" />,
											cols: 6,
										},
									].map((item, index) => (
										<Grid item xs={12} sm={item.cols} key={index}>
											<InfoRow label={item.label} value={item.value} icon={item.icon} />
										</Grid>
									))}

									<Grid item xs={12} sm={6}>
										<Typography variant="body2" color="text.secondary">
											Active
										</Typography>
										<Chip
											label={previewData?.paycentral?.isActive ? "Active" : "Inactive"}
											color={previewData?.paycentral?.isActive ? "success" : "default"}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<Typography variant="body2" color="text.secondary">
											Auto Invoice
										</Typography>
										<Chip
											label={previewData?.paycentral?.isAutoInvoice ? "Enabled" : "Disabled"}
											color={previewData?.paycentral?.isAutoInvoice ? "success" : "default"}
										/>
									</Grid>
								</Grid>
							</Box>

						</CardContent>
					</Card>
				</Grid>
			)}

			{isPaycentral && (
				<Grid item xs={12} md={4}>
					<Card
						sx={{
							height: "100%",
							background:
								"linear-gradient(135deg, #466bb3 0%, #22419e 100%)",
							boxShadow: "0 20px 60px rgba(34, 65, 158, 0.3)",
						}}
					>
						<CardContent sx={{ p: 3 }}>

							<Stack
								direction="row"
								alignItems="center"
								spacing={1.5}
								sx={{ mb: 2 }}
							>
								<Box
									sx={{
										bgcolor: "white",
										p: 1,
										borderRadius: 1,
										display: "flex",
									}}
								>
									<MyCreditCardIcon
										sx={{
											fontSize: 24,
											color: "var(--bs-primary)",
										}}
									/>
								</Box>
								<Typography
									variant="h6"
									fontWeight={600}
									sx={{ color: "white" }}
								>
									Cost Information
								</Typography>
							</Stack>
							{/* Subscription Fees */}
							<Box
								sx={{
									bgcolor: "rgba(255,255,255,0.15)",
									p: 2,
									borderRadius: 1,
									mt: 2,
									backdropFilter: "blur(10px)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									sx={{ mb: 1.5, color: "white" }}
								>
									Subscription Fees
								</Typography>

								<Stack spacing={1.5}>
									{[
										{
											label: "One-Time Platform Setup Fees",
											value: previewData?.paycentralCost?.oneTimeSetupFee,
										},
										{
											label: "Ongoing Maintenance & Support",
											value: previewData?.paycentralCost?.ongoingMaintenanceFee,
										},
										{
											label: "New Merchant Setup Fee",
											value: previewData?.paycentralCost?.newMerchantSetupFee,
										},
										{
											label: "Ongoing Maintenance per Merchant",
											value: previewData?.paycentralCost?.ongoingMaintenancePerMerchantFee,
										},
									].map((item, index) => (
										<Box key={index} sx={{ py: 1 }}>
											<Typography
												variant="body2"
												sx={{
													color: "rgba(255,255,255,0.8)",
													mb: 0.5,
												}}
											>
												{item.label}
											</Typography>

											<Stack direction="row" spacing={1} alignItems="center">
												<MyAttachMoneyIcon fontSize="small" />
												<Typography
													variant="body1"
													fontWeight={600}
													sx={{ color: "white" }}
												>
													{item.value
														? `${currencyDetails?.symbol} ${Number(item.value).toFixed(2)}`
														: "-"}
												</Typography>
											</Stack>
										</Box>
									))}
								</Stack>
							</Box>

						</CardContent>
					</Card>
				</Grid>
			)}

			{/* KYC Information */}
			{isKyc && (
				<Grid item xs={12}>
					<Card
						sx={{
							height: "100%",
							boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
						}}
					>
						<CardContent sx={{ p: 3 }}>
							{/* Inner Box (same as Paycentral style) */}
							<Box
								sx={{
									bgcolor: "grey.50",
									p: 2.5,
									borderRadius: 1,
									mb: 3,
									boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									color="var(--bs-primary)"
									sx={{ mb: 1.5 }}
								>
									KYC Details
								</Typography>

								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<InfoRow
											label="User Name"
											value={previewData?.kyc?.kycUserName}
											icon={<MyPersonIcon fontSize="small" />}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<InfoRow
											label="Country"
											value={selectedKycCountry?.name || previewData?.kyc?.kycCountry}
											icon={<MyLanguageIcon fontSize="small" />}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<InfoRow
											label="Company Name"
											value={previewData?.kyc?.kycCompanyName}
											icon={<MyBusinessIcon fontSize="small" />}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<InfoRow
											label="Company Email"
											value={previewData?.kyc?.kycCompanyEmail}
											icon={<MyEmailIcon fontSize="small" />}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<InfoRow
											label="Phone Number"
											value={previewData?.common?.companyPhoneNumber}
											icon={<MyPhoneIcon fontSize="small" />}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<InfoRow
											label="Domain URL"
											value={previewData?.kyc?.kycDomainUrl}
											icon={<MyLanguageIcon fontSize="small" />}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<InfoRow
											label="Auto Pay"
											value={previewData?.kyc?.kycIsAutoPay ? "Enabled" : "Disabled"}
											icon={<MyCheckCircleIcon fontSize="small" />}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<InfoRow
											label="Plan"
											value={selectedKycPlan?.name}
											icon={<MyCreditCardIcon fontSize="small" />}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<InfoRow
											label="Billing Cycle"
											value={previewData?.kyc?.kycBillingCycle}
											icon={<MyCalendarIcon fontSize="small" />}
										/>
									</Grid>
								</Grid>
							</Box>

						</CardContent>
					</Card>
				</Grid>
			)}

			{/* tripola Company Details */}
			{isTripola && (
				<Grid item xs={12} md={8}>
					<Card
						sx={{
							height: "100%",
							boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
						}}
					>
						<CardContent sx={{ p: 3 }}>
							<Box
								sx={{
									bgcolor: "grey.50",
									p: 2.5,
									borderRadius: 1,
									mb: 3,
									boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									color="var(--bs-primary)"
									sx={{ mb: 1.5 }}
								>
									Tripola Company Details
								</Typography>
								<Grid container spacing={2}>
									{[
										{
											label: "Company Name",
											value: previewData?.tripola?.tripolaCompanyName,
											icon: (
												<MyBusinessIcon fontSize="small" />
											),
											cols: 6,
										},
										{
											label: "Company Email",
											value: previewData?.tripola?.tripolaCompanyEmail,
											icon: <MyEmailIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Domain URL",
											value: previewData?.tripola?.tripolaDomainUrl,
											icon: (
												<MyLanguageIcon fontSize="small" />
											),
											cols: 6,
										},
										{
											label: "Phone Number",
											value: previewData?.tripola?.tripolaCompanyPhoneNumber,
											icon: <MyPhoneIcon fontSize="small" />,
											cols: 6,
										},
										{
											label: "Address",
											value: previewData?.common.companyAddress,
											icon: (
												<MyBusinessIcon fontSize="small" />
											),
											cols: 12,
										},
										{
											label: "City",
											value: previewData?.tripola?.tripolaCompanyCity,
											icon: (
												<MyBusinessIcon fontSize="small" />
											),
											cols: 12,
										},
										{
											label: "Zip Code",
											value: previewData?.tripola?.tripolaCompanyZipCode,
											icon: (
												<MyLocationIcon fontSize="small" />
											),
											cols: 4,
										},
										{
											label: "Country",
											value: previewData?.common.companyCountry,
											icon: (
												<MyLanguageIcon fontSize="small" />
											),
											cols: 4,
										},
										{
											label: "State",
											value: previewData?.common.companyState,
											icon: <MyMapIcon fontSize="small" />,
											cols: 4,
										},
									].map((item, index) => (
										<Grid
											item
											xs={12}
											sm={item.cols}
											key={index}
										>
											<InfoRow
												label={item.label}
												value={item.value}
												icon={item.icon}
											/>
										</Grid>
									))}
								</Grid>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={4} mt={2}>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ mb: 0.5 }}
										>
											Domain Verification
										</Typography>
										<Chip
											icon={
												previewData?.tripola?.tripolaIsVerified ? (
													<MyVerifiedIcon />
												) : (
													<MyCancelIcon />
												)
											}
											label={
												previewData?.tripola?.tripolaIsVerified
													? "Verified"
													: "Unverified"
											}
											color={
												previewData?.tripola?.tripolaIsVerified
													? "success"
													: "error"
											}
											size="medium"
										/>
									</Grid>
									<Grid item xs={12} sm={4} mt={2}>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ mb: 0.5 }}
										>
											Auto Invoice
										</Typography>
										<Chip
											icon={
												safeBoolean(
													previewData?.tripolaPlan?.tripolaIsAutoInvoice,
												) ? (
													<MyCheckCircleIcon className="text-success" />
												) : (
													<MyCancelIcon className="text-danger" />
												)
											}
											label={
												safeBoolean(previewData?.tripolaPlan?.tripolaIsAutoInvoice)
													? "Enabled"
													: "Disabled"
											}
											className={
												safeBoolean(previewData?.tripolaPlan?.tripolaIsAutoInvoice)
													? "text-success"
													: "text-danger"
											}
											sx={{ fontWeight: 600 }}
										/>
									</Grid>
									<Grid item xs={12} sm={4} mt={2}>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ mb: 0.5 }}
										>
											Auto Pay
										</Typography>
										<Chip
											icon={
												safeBoolean(
													previewData?.tripolaPlan?.tripolaIsAutoPay,
												) ? (
													<MyCheckCircleIcon className="text-success" />
												) : (
													<MyCancelIcon className="text-danger" />
												)
											}
											label={
												safeBoolean(previewData?.tripolaPlan?.tripolaIsAutoPay)
													? "Enabled"
													: "Disabled"
											}
											className={
												safeBoolean(previewData?.tripolaPlan?.tripolaIsAutoPay)
													? "text-success"
													: "text-danger"
											}
											sx={{ fontWeight: 600 }}
										/>
									</Grid>

								</Grid>
							</Box>
						</CardContent>
					</Card>
				</Grid>
			)}

			{/* Tripola Plan Information Card */}
			{isTripola && (
				<Grid item xs={12} md={4}>
					<Card
						sx={{
							height: "100%",
							background:
								"linear-gradient(135deg, #466bb3 0%, #22419e 100%)",
							boxShadow: "0 20px 60px rgba(34, 65, 158, 0.3)",
						}}
					>
						<CardContent sx={{ p: 3 }}>
							<Stack
								direction="row"
								alignItems="center"
								spacing={1.5}
								sx={{ mb: 2 }}
							>
								<Box
									sx={{
										bgcolor: "white",
										p: 1,
										borderRadius: 1,
										display: "flex",
									}}
								>
									<MyCreditCardIcon
										sx={{
											fontSize: 24,
											color: "var(--bs-primary)",
										}}
									/>
								</Box>
								<Typography
									variant="h6"
									fontWeight={600}
									sx={{ color: "white" }}
								>
									Tripola Plan Information
								</Typography>
							</Stack>

							{/* Plan Details */}
							<Box
								sx={{
									bgcolor: "rgba(255,255,255,0.15)",
									p: 2,
									borderRadius: 1,
									mb: 2,
									backdropFilter: "blur(10px)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									sx={{ mb: 1.5, color: "white" }}
								>
									Plan Details{isPlanChanged ? " (upcoming)" : ""}
								</Typography>
								<Stack spacing={1.5}>
									{[
										{
											label: "Plan Name",
											value: previewData?.tripolaPlan?.tripolaPlanId
												? selectedTripolaPlan?.plan_name || "-"
												: "-",
											icon: (
												<MyCreditCardIcon fontSize="small" />
											),
											variant: "body1",
										},
									].map((item, index) => (
										<Box sx={{ py: 1 }} key={index}>
											<Typography
												variant="body2"
												sx={{
													color: "rgba(255,255,255,0.8)",
													display: "block",
													mb: 0.5,
												}}
											>
												{item.label}
											</Typography>
											<Stack
												direction="row"
												spacing={1}
												alignItems="center"
											>
												<Box
													sx={{
														color: "white",
														display: "flex",
														minWidth: 20,
													}}
												>
													{item.icon}
												</Box>
												<Typography
													variant={item.variant}
													fontWeight={
														item.variant === "h5"
															? 700
															: 600
													}
													sx={{ color: "white" }}
												>
													{item.value}
												</Typography>
											</Stack>
										</Box>
									))}
								</Stack>
							</Box>

							{/* Billing Information */}
							<Box
								sx={{
									bgcolor: "rgba(255,255,255,0.15)",
									p: 2,
									borderRadius: 1,
									mb: 2,
									backdropFilter: "blur(10px)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									sx={{ mb: 1.5, color: "white" }}
								>
									Billing Information
									{isPlanChanged ? " (upcoming)" : ""}
								</Typography>
								<Stack spacing={1.5}>
									{[
										{
											label: "Billing Cycle",
											value: previewData?.tripolaPlan?.tripolaBillingCycle || "-",
											icon: (
												<MyCalendarIcon fontSize="small" />
											),
										},
									].map((item, index) => (
										<Box sx={{ py: 1 }} key={index}>
											<Typography
												variant="body2"
												sx={{
													color: "rgba(255,255,255,0.8)",
													display: "block",
													mb: 0.5,
												}}
											>
												{item.label}
											</Typography>
											<Stack
												direction="row"
												spacing={1}
												alignItems="center"
											>
												<Box
													sx={{
														color: "white",
														display: "flex",
														minWidth: 20,
													}}
												>
													{item.icon}
												</Box>
												<Typography
													variant="body1"
													fontWeight={600}
													sx={{
														textTransform: "capitalize",
														color: "white",
													}}
												>
													{item.value}
												</Typography>
											</Stack>
										</Box>
									))}
								</Stack>
							</Box>
						</CardContent>
					</Card>
				</Grid>
			)}
		</Grid>
	);
};

export default TenantCreationPreview;
